/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {MarkdownEl} from 'common/components/markdown';

import {CodeTipEl} from '../components/code-tip';
import {Section, SubSection} from './utils';

export function TypographySection() {
  return (
    <Section title='Typography'>
      <SubSection title='Using Type'>
        <p sel='.desc'>
          <span sel='.strong'>TLDR:</span> It’s mostly determined initially by line length, then by importance.
        </p>

        <p sel='.desc'>
          <span sel='.strong'>Title Case for short stuff.</span>
          {` `}
          Buttons shouldn’t be a sentence, so they shouldn’t use sentence case. Label’s shouldn’t be a sentence long, so
          they shouldn’t be sentence case.
        </p>

        <p sel='.desc'>
          <span sel='.strong'>Sentence case when lots of words go together.</span>
          {` `}
          If you’re saying an actual sentence, use sentence case. This means descriptions, and details mostly. If you’re
          describing something after showing it and “titling” it in some way, then use sentence case.
        </p>

        <p sel='.desc'>
          <span sel='.strong'>For example:</span>
          {` `}
          You have an input field. The top label should be set in Title Case, and the placeholder and any description
          text should be set in sentence case.
          {` `}
          <span sel='.strong'>Do:</span>
          {` `}
          Delete File, Export Cohort
          {` `}
          <span sel='.strong'>Do not:</span>
          {` `}
          Delete file, DELETE FILE, delete file
        </p>
      </SubSection>

      <SubSection title='Type Weights'>
        <div sel='.example.block'>
          <p sel='.mp-font-weight-regular'>.mp-font-weight-regular | Mixpanel helps you learn from your data</p>
        </div>
        <div sel='.example.block'>
          <p sel='.mp-font-weight-medium'>.mp-font-weight-medium | Mixpanel helps you learn from your data</p>
        </div>
        <div sel='.example.block'>
          <p sel='.mp-font-weight-bold'>.mp-font-weight-bold | Mixpanel helps you learn from your data</p>
        </div>
      </SubSection>

      <SubSection title='Type Sizes'>
        <p sel='.desc'>
          By limiting our font catalogue to just six sizes, we're able to reduce page load times and further reinforce
          UI patterns. Our baseline is 14px (medium), and our smallest point size is ALL CAPS 11px. There will be
          instances where micro-copy is required, which has not been accounted for in common at this time.
        </p>

        <div sel='.example.block'>
          <p sel='.mp-font-size-xxl'>
            class: .mp-font-size-xxl | mixin: font-size $font-size-xxl | Mixpanel helps you learn from your data
          </p>
        </div>
        <div sel='.example.block'>
          <p sel='.mp-font-size-xl'>
            class: .mp-font-size-xl | mixin: font-size $font-size-xl | Mixpanel helps you learn from your data
          </p>
        </div>
        <div sel='.example.block'>
          <p sel='.mp-font-size-large'>
            class: .mp-font-size-large | mixin: font-size $font-size-large | Mixpanel helps you learn from your data
          </p>
        </div>
        <div sel='.example.block'>
          <p sel='.mp-font-size-medium'>
            class: .mp-font-size-medium | mixin: font-size $font-size-medium | Mixpanel helps you learn from your data
          </p>
        </div>
        <div sel='.example.block'>
          <p sel='.mp-font-size-small'>
            class: .mp-font-size-small | mixin: font-size $font-size-small | Mixpanel helps you learn from your data
          </p>
        </div>
        <div sel='.example.block'>
          <p sel='.mp-font-size-xs'>
            class: .mp-font-size-xs | mixin: font-size $font-size-xs | Mixpanel helps you learn from your data
          </p>
        </div>

        <br />
        <p sel='.desc'>
          Note that the paragraph style uses font-size-medium, and adds in additional color and line-height styling.
        </p>

        <div sel='.example.block'>
          <p sel='.mp-font-paragraph'>.mp-font-paragraph | Mixpanel helps you learn from your data</p>
        </div>
      </SubSection>

      <SubSection title='mp-markdown' autoDoc={true}>
        <p sel='.desc'>
          Accepts markdown formatted text and displays the resulting HTML. It does not use the Shadow DOM so any outside
          styles will be applied to the resulting markup.
        </p>

        <CodeTipEl sel='.example'>
          <MarkdownEl attrs={{text: `# Here is an example`}} />
        </CodeTipEl>
      </SubSection>
    </Section>
  );
}
